import { useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import { faPlus, faSearch, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Banner, Button, ButtonText, FormFeedback } from '@morressier/alembic';
import { useFieldArray, useFormContext } from 'react-hook-form';
import StepHeader from 'src/pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { Layout } from 'src/components/Layout';
import ExpertsSearch from './ExpertsSearch';
import { IInviteExperts, IInviteExpertsForm } from './commonTypes';
import { IUser } from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import { fetchExpertsInviteDetails } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';

interface Props {
  currentStep: number;
  totalSteps: number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  closeModal: () => void;
}

export type MakeReadOnly = {
  readOnly: boolean;
  index: number;
};
const handleEmptyReviewer = (experts: IInviteExperts[]) => {
  return experts.filter(expert => !expert.id);
};
const DEFAULT_EMPTY_RECORD = { id: '', name: '', email: '' };
const AddExperts = ({
  currentStep,
  totalSteps,
  goToNextStep,
  closeModal
}: Props) => {
  const {
    control,
    register,
    formState: { errors },
    clearErrors,
    trigger,
    getValues,
    setValue,
    setError
  } = useFormContext<IInviteExpertsForm>();

  const { fields, append, remove, update } = useFieldArray({
    name: 'experts',
    control: control
  });
  const [isReadOnly, setIsReadOnly] = useState<MakeReadOnly>({
    readOnly: false,
    index: 0
  });
  const errorRef = useRef<HTMLDivElement | null>(null);
  const { submissionContextId, submissionId } = useSubmissionData();
  const removeReviewer = (index: number) => {
    clearErrors(`experts.${index}`);
    remove(index);

    if (fields.length === 1) {
      append(DEFAULT_EMPTY_RECORD);
    }
  };
  const { data } = useQuery({
    queryKey: [
      'board-member-experts-invite-list',
      { submissionContextId, submissionId }
    ],
    queryFn: () =>
      submissionId && submissionContextId
        ? fetchExpertsInviteDetails(
            submissionContextId,
            submissionId,
            'early-assessments'
          )
        : null
  });
  const existingExperts: IInviteExperts[] | Pick<IInviteExperts, 'id'>[] = data
    ? data.items.map(item => item.expert)
    : [];
  const findDuplicateIndices = (experts: IInviteExperts[]) => {
    const emailSet = new Set(existingExperts.map(reviewer => reviewer.id));
    const duplicateIndices: number[] = [];

    experts.forEach((reviewer, index) => {
      if (emailSet.has(reviewer.id)) {
        duplicateIndices.push(index);
      }
    });

    return duplicateIndices;
  };

  const checkDuplicateReviewer = (experts: IInviteExperts[]) => {
    const duplicateIndices = findDuplicateIndices(experts);

    if (duplicateIndices.length > 0) {
      duplicateIndices.forEach(index => {
        setError(`experts.${index}.id`, {
          type: 'duplicate',
          message: 'This Expert is already associated with this Submission.'
        });
      });
    }
  };
  const handleNextStep = async () => {
    await trigger(['experts']);
    const experts = getValues('experts');
    checkDuplicateReviewer(experts);
    const hadEmptyReviewer = handleEmptyReviewer(experts);

    if (!errors.experts?.length && !hadEmptyReviewer.length) {
      goToNextStep();
    } else {
      // scroll to first error
      const firstError = errorRef.current?.querySelector(
        '[id^="expert-error-"]'
      );
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const onExpertsSelect = (boardMember: IUser, index: number) => {
    // update the selected org values in form
    setValue(
      `experts.${index}.name`,
      `${boardMember.firstName} ${boardMember.lastName}`,
      {
        shouldValidate: true,
        shouldDirty: true
      }
    );
    setValue(`experts.${index}.id`, boardMember.id, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`experts.${index}.email`, boardMember.email, {
      shouldValidate: true,
      shouldDirty: true
    });
    setIsReadOnly({ readOnly: true, index });
  };

  return (
    <>
      <Layout width="960px">
        <Flex flexDirection="column" gap={8}>
          <Flex flexDirection="column">
            <StepHeader
              currentStep={currentStep}
              totalSteps={totalSteps}
              heading="Select Experts"
              description="Search for board members or experts to assess this Submission and provide recommendations."
            />

            <Banner
              variant="attention"
              heading="For your attention"
              content={`Expert Support is only available in the Initial Assessment phase. Select an expert and provide instructions to proceed.`}
            />
          </Flex>

          <Flex
            gap={12}
            padding={6}
            backgroundColor="surfacePrimaryFill"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex flexDirection="column" gap={8} ref={errorRef} width="100%">
              {fields.map((field, index) => (
                <Flex
                  key={field.id}
                  gap={6}
                  padding={8}
                  flexDirection="column"
                  justifyContent="flex-end"
                  backgroundColor="surfacePrimaryFill"
                  id={
                    Object.keys(errors.experts?.[index] || {}).length > 0
                      ? `expert-error-${index}`
                      : `expert-${index}`
                  }
                >
                  <Flex flexDirection="column" gap={2}>
                    <FieldLabel
                      label="Search for Expert"
                      subText="Search through available experts affiliated to this Journal."
                      isRequired
                    />
                    <ExpertsSearch
                      {...register(`experts.${index}.name`)}
                      placeholder="Search for experts"
                      iconLeft={<FontAwesomeIcon icon={faSearch} />}
                      onExpertsSelect={boardMember =>
                        onExpertsSelect(boardMember, index)
                      }
                      isInvalid={Boolean(errors?.experts?.[index]?.id)}
                      onInputClear={() => {
                        update(index, DEFAULT_EMPTY_RECORD);
                        setIsReadOnly({ readOnly: false, index });
                      }}
                      isReadOnlyOption={isReadOnly}
                      index={index}
                      selectedExpert={getValues(`experts.${index}.name`)}
                    />
                    {errors.experts?.[index]?.id && (
                      <FormFeedback variant="error">
                        {errors.experts?.[index]?.id?.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex justifyContent="flex-end">
                    <ButtonText
                      variant="secondary"
                      alignSelf="baseline"
                      iconLeft={<FontAwesomeIcon icon={faTrash} />}
                      onClick={() => {
                        removeReviewer(index);
                        setIsReadOnly({ readOnly: false, index });
                      }}
                    >
                      Delete Expert
                    </ButtonText>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>

          <Flex justifyContent="flex-end">
            <ButtonText
              alignSelf="baseline"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => append(DEFAULT_EMPTY_RECORD)}
            >
              Add Another Expert
            </ButtonText>
          </Flex>
        </Flex>
      </Layout>
      <Flex
        paddingY={4}
        paddingX={12}
        bottom={0}
        zIndex={1}
        justifyContent="center"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex width="100%" justifyContent="space-between">
          <ButtonText variant="caution" onClick={closeModal}>
            Exit
          </ButtonText>

          <Button variant="primary" type="button" onClick={handleNextStep}>
            Next
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default AddExperts;
